import { Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './lib/auth';
import { Spinner } from './components/ui';
import { AdminLayout, PlatformLayout, WorkerLayout } from './components/layouts';
import { ForgotPasswordPage, HandoffPage, InvitePage, LoginPage, MfaSetupPage, NotFoundPage, ResetPasswordPage, SignupPage } from './pages/auth';
import { WorkerHome } from './pages/worker/Home';
import { DevMailPage } from './pages/DevMail';
import { WorkerHistory, WorkerRecordDetail } from './pages/worker/History';
import { WorkerCorrectionDetail, WorkerIncidentDetail, WorkerRequests } from './pages/worker/Requests';
import { WorkerAbsences } from './pages/worker/Absences';
import { NotificationsPage, PrivacyPage, WorkerCalendar, WorkerDocuments, WorkerProfile } from './pages/worker/Profile';
import { LandingPage } from './pages/Landing';
import { LegalPage } from './pages/Legal';
import { ShareLinkHost } from './components/share-link';
import { lazyWithReload } from './lib/app-update';

const Admin = lazyWithReload(() => import('./pages/admin'));
const Kiosk = lazyWithReload(() => import('./pages/kiosk/Kiosk'));
const Platform = lazyWithReload(() => import('./pages/platform/Platform'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (me.mfaSetupRequired && loc.pathname !== '/mfa-setup') return <Navigate to="/mfa-setup" replace />;
  return <>{children}</>;
}

function Home() {
  const { me, loading, isAdminArea } = useAuth();
  if (loading) return <Spinner />;
  // Sin sesión: página pública de presentación
  if (!me) return <LandingPage />;
  if (me.mfaSetupRequired) return <Navigate to="/mfa-setup" replace />;
  if (!me.company && me.user.isSuperadmin) return <Navigate to="/platform" replace />;
  if (isAdminArea) return <Navigate to="/admin" replace />;
  if (me.company?.employee) return <Navigate to="/app" replace />;
  return <Navigate to="/app/profile" replace />;
}

const lazyWrap = (el: ReactNode) => <Suspense fallback={<Spinner />}>{el}</Suspense>;

export function App() {
  return (
    <BrowserRouter>
      <ShareLinkHost />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/handoff" element={<HandoffPage />} />
        <Route path="/legal" element={<Navigate to="/legal/aviso-legal" replace />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        {import.meta.env.DEV && <Route path="/dev/mail" element={<DevMailPage />} />}
        <Route
          path="/mfa-setup"
          element={
            <RequireAuth>
              <MfaSetupPage />
            </RequireAuth>
          }
        />
        <Route path="/kiosk/*" element={lazyWrap(<Kiosk />)} />
        <Route path="/" element={<Home />} />
        <Route
          path="/app"
          element={
            <RequireAuth>
              <WorkerLayout />
            </RequireAuth>
          }
        >
          <Route index element={<WorkerHome />} />
          <Route path="history" element={<WorkerHistory />} />
          <Route path="history/:id" element={<WorkerRecordDetail />} />
          <Route path="requests" element={<WorkerRequests />} />
          <Route path="requests/incidents/:id" element={<WorkerIncidentDetail />} />
          <Route path="requests/corrections/:id" element={<WorkerCorrectionDetail />} />
          <Route path="absences" element={<WorkerAbsences />} />
          <Route path="documents" element={<WorkerDocuments />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="calendar" element={<WorkerCalendar />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="profile" element={<WorkerProfile />} />
        </Route>
        <Route
          path="/admin/*"
          element={
            <RequireAuth>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route path="*" element={lazyWrap(<Admin />)} />
        </Route>
        <Route
          path="/platform/*"
          element={
            <RequireAuth>
              <PlatformLayout />
            </RequireAuth>
          }
        >
          <Route path="*" element={lazyWrap(<Platform />)} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
